import React, { useEffect, useState } from "react";
import { Button, Link } from "@sscs/ui";
import { CancelIcon } from "@sscs/ui/icons";
import {
  CONSENT_TO_ANALYTICS_NAME,
  CONSENT_TO_FUNCTIONAL_COOKIES_NAME,
  CookieConsentEnum,
  getClassName,
  getCookie,
  setCookie,
} from "@sscs/utils";
import { COOKIE_POLICY_ROUTE } from "Constants";
import { getRouteBasename } from "Utils";
import styles from "./cookieConsentBanner.module.scss";
import CookieConsentDialog from "./CookieConsentDialog";

const basename = getRouteBasename();

const CONSENT_TO_ANALYTICS_EXPIRES_DAYS = 365;

export type CookiePreferences = Record<ConsentCookieName, boolean | undefined>;

export type ConsentCookieName =
  | typeof CONSENT_TO_FUNCTIONAL_COOKIES_NAME
  | typeof CONSENT_TO_ANALYTICS_NAME;

export type CookieConsentBannerProps = {
  analyticsUsed: boolean;
  gtmUsed: boolean;
  className?: string;
  alwaysShow?: boolean;
};

const CookieConsentBanner = ({
  analyticsUsed,
  gtmUsed,
  className,
  alwaysShow,
}: CookieConsentBannerProps) => {
  const [showBanner, setShowBanner] = useState(false);
  const [manageCookiesModalIsOpen, setManageCookiesModalIsOpen] =
    useState(false);

  useEffect(() => {
    const missingFunctionalCookiesConsent =
      getCookie(CONSENT_TO_FUNCTIONAL_COOKIES_NAME) ===
      CookieConsentEnum.Undefined;
    const missingAnalyticsConsent =
      getCookie(CONSENT_TO_ANALYTICS_NAME) === CookieConsentEnum.Undefined;

    if (analyticsUsed) {
      setShowBanner(missingFunctionalCookiesConsent || missingAnalyticsConsent);
    } else {
      setShowBanner(missingFunctionalCookiesConsent);
    }
  }, [analyticsUsed]);

  const acceptCookie = (cookieName: ConsentCookieName) => {
    setCookie(
      cookieName,
      CookieConsentEnum.Accepted,
      CONSENT_TO_ANALYTICS_EXPIRES_DAYS,
    );
  };

  const rejectCookie = (cookieName: ConsentCookieName) => {
    setCookie(
      cookieName,
      CookieConsentEnum.Rejected,
      CONSENT_TO_ANALYTICS_EXPIRES_DAYS,
    );
  };

  const acceptAllCookies = () => {
    acceptCookie(CONSENT_TO_FUNCTIONAL_COOKIES_NAME);
    if (analyticsUsed) {
      acceptCookie(CONSENT_TO_ANALYTICS_NAME);
    }

    setShowBanner(false);
    window.location.reload();
  };

  const saveCustomPreferences = (
    cookiePreferences: Record<ConsentCookieName, boolean | undefined>,
  ) => {
    for (const [cookieName, cookieAccepted] of Object.entries(
      cookiePreferences,
    )) {
      if (cookieAccepted) {
        acceptCookie(cookieName as ConsentCookieName);
      } else {
        rejectCookie(cookieName as ConsentCookieName);
      }
    }
    setShowBanner(false);
    window.location.reload();
  };

  const handleClose = () => {
    // if there are no unnecessary cookies to accept, accept necessary cookies when dismissing the banner
    if (!analyticsUsed) {
      acceptCookie(CONSENT_TO_FUNCTIONAL_COOKIES_NAME);
    }

    setShowBanner(false);
  };

  const openManageCookiesModal = () => {
    setManageCookiesModalIsOpen(true);
  };

  const closeManageCookiesModal = () => {
    setManageCookiesModalIsOpen(false);
  };

  if (!showBanner && !alwaysShow) {
    return null;
  }

  const cls = getClassName(styles["banner"], className);

  return (
    <div className={cls}>
      <CookieConsentDialog
        isOpen={manageCookiesModalIsOpen}
        acceptAllCookies={acceptAllCookies}
        saveCustomPreferences={saveCustomPreferences}
        analyticsUsed={analyticsUsed}
        gtmUsed={gtmUsed}
        onClose={closeManageCookiesModal}
      />
      <CancelIcon
        className={styles["close-icon"]}
        color="info"
        onClick={handleClose}
      />
      <div className={styles["text"]}>
        This website uses cookies to ensure the best website experience. By
        continuing to use this website you are giving your consent to cookies
        being used. Detailed information about our use of cookies is{" "}
        <Link href={`${basename}/${COOKIE_POLICY_ROUTE}`}>here</Link>.
      </div>
      {analyticsUsed && (
        <div className={styles["buttons"]}>
          <Button
            color="secondary"
            onClick={acceptAllCookies}
            aria-label="accept all cookies"
          >
            Accept
          </Button>
          <Button
            color="secondary"
            onClick={openManageCookiesModal}
            aria-label="manage cookies"
          >
            Manage cookies
          </Button>
        </div>
      )}
    </div>
  );
};

export default CookieConsentBanner;
